import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { FiDownload } from "react-icons/fi";
import {
  SeV2QuestionKind,
  SeV2QuestionStatus,
  useGetBuyerSupplierInformationQuery,
  useGetBuyerSeV2QuestionsQuery,
  type SeV2QuestionDto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useGoToEventTab } from "../useGoToEventTab";
import { InternalNotesSection } from "../InternalNotesSection";
import { communicationSubTabs, parseCommunicationSubTab } from "./communicationSubTab";
import { BuyerQuestionInbox } from "./BuyerQuestionInbox";
import { ClarificationsPanel } from "./ClarificationsPanel";
import { downloadSeV2QaReport } from "./downloadSeV2QaReport";
import { InformationComposer } from "./InformationComposer";
import { PublicFeedPreview } from "./PublicFeedPreview";

const groupByPublishedInformation = (questions: SeV2QuestionDto[]) => {
  const byInformationId = new Map<string, SeV2QuestionDto[]>();
  questions.forEach((question) => {
    if (!question.publishedInformationId) return;
    byInformationId.set(question.publishedInformationId, [
      ...(byInformationId.get(question.publishedInformationId) ?? []),
      question,
    ]);
  });
  return byInformationId;
};

const CountBadge = ({ count, needsAttention }: { count: number; needsAttention?: boolean }) => {
  if (count <= 0) return null;
  return (
    <Badge ml="2" rounded="full" px="2" colorScheme={needsAttention ? "orange" : "gray"}>
      {count}
    </Badge>
  );
};

export const CommunicationTab = ({ event }: { event: SourcingEventV2Dto }) => {
  const eventId = event.id;
  const eventTitle = event.eventData.title;
  const { t } = useTranslation();
  const displayer = useApiError();
  const [searchParams] = useSearchParams();
  const goToTab = useGoToEventTab();
  const [isDownloadingReport, setDownloadingReport] = useState(false);

  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      await downloadSeV2QaReport({
        eventId,
        fileName: `${eventTitle} - ${t("Questions and announcements")}`,
      });
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setDownloadingReport(false);
    }
  };

  const {
    data: questionsData,
    error: questionsError,
    isLoading: questionsLoading,
  } = useGetBuyerSeV2QuestionsQuery({
    eventId,
  });
  const {
    data: announcementsData,
    error: announcementsError,
    isLoading: announcementsLoading,
  } = useGetBuyerSupplierInformationQuery({ eventId });

  useEffect(() => {
    if (questionsError) displayer.triggerSoft(questionsError);
  }, [questionsError, displayer]);
  useEffect(() => {
    if (announcementsError) displayer.triggerSoft(announcementsError);
  }, [announcementsError, displayer]);

  if (questionsLoading || announcementsLoading) {
    return (
      <Box py="10" textAlign="center">
        <Spinner color="smPrimary" />
      </Box>
    );
  }

  const isPublic = event.eventContext.type === "Public";
  const subTabs = communicationSubTabs(event);
  const activeSubTab = parseCommunicationSubTab(searchParams.get("sub"), subTabs);
  const threads = questionsData?.questions ?? [];
  // Supplier questions and buyer-opened clarification dialogs share an aggregate but not a tab: a question is
  // competition Q&A that may end up published to all, a clarification is a private 1:1 exchange (FOA § 23-5).
  const questions = threads.filter((q) => q.kind === SeV2QuestionKind.Question);
  const clarifications = threads.filter((q) => q.kind === SeV2QuestionKind.Clarification);
  const announcements = announcementsData?.supplierInformation ?? [];
  const unanswered = questions.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;
  const awaitingTenderer = clarifications.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;

  const questionsByInformationId = groupByPublishedInformation(questions);
  const qaAnnouncements = announcements.filter((a) => questionsByInformationId.has(a.id));
  const infoAnnouncements = isPublic
    ? announcements.filter((a) => !questionsByInformationId.has(a.id))
    : announcements;

  return (
    <Tabs
      colorScheme="teal"
      isLazy
      index={subTabs.indexOf(activeSubTab)}
      onChange={(i) => goToTab("communication", subTabs[i])}
    >
      <Flex align="flex-end" justify="space-between" gap="4" borderBottom="1px solid" borderColor="smBorder">
        <Box overflowX="auto">
          <TabList minW="max-content" borderColor="transparent">
            <Tab>
              {t("Questions")}
              <CountBadge count={unanswered} needsAttention />
            </Tab>
            {isPublic && (
              <Tab>
                {t("Public Q&A")}
                <CountBadge count={qaAnnouncements.length} />
              </Tab>
            )}
            <Tab>
              {t("Information to tenderers")}
              <CountBadge count={infoAnnouncements.length} />
            </Tab>
            <Tab>
              {t("Clarifications")}
              <CountBadge count={awaitingTenderer} needsAttention />
            </Tab>
            <Tab>
              {t("Internal conversation")}
              <CountBadge count={event.internalConversation?.length ?? 0} />
            </Tab>
          </TabList>
        </Box>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gray"
          flexShrink={0}
          mb="2"
          leftIcon={<Icon as={FiDownload} />}
          isLoading={isDownloadingReport}
          onClick={downloadReport}
        >
          {t("Download Q&A report")}
        </Button>
      </Flex>
      <TabPanels>
        <TabPanel px="0">
          <BuyerQuestionInbox eventId={eventId} questions={questions} />
        </TabPanel>
        {isPublic && (
          <TabPanel px="0">
            <PublicFeedPreview
              eventId={eventId}
              announcements={qaAnnouncements}
              questionsByInformationId={questionsByInformationId}
            />
          </TabPanel>
        )}
        <TabPanel px="0">
          <InformationComposer
            eventId={eventId}
            announcements={infoAnnouncements}
            questionsByInformationId={questionsByInformationId}
          />
        </TabPanel>
        <TabPanel px="0">
          <ClarificationsPanel event={event} questions={clarifications} />
        </TabPanel>
        <TabPanel px="0">
          <InternalNotesSection event={event} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
